import type { AnchorHTMLAttributes, ReactElement } from 'react'

import { MDXRemote, MDXRemoteSerializeResult } from 'next-mdx-remote'
import { serialize } from 'next-mdx-remote/serialize'
import Head from 'next/head'
import Link from 'next/link'

import { format } from 'date-fns'

import { Container } from '@ui/components/Container'
import { RecommendedOffer } from '@ui/components/Content'
import {
  AdvertiserDisclosure,
  AffiliateDisclosure,
} from '@ui/components/Disclosure'
import { Seo } from '@ui/components/seo'
import { MarketingLayout } from '@ui/layout/MarketingLayout'
import { canonicalMarketingUrl } from '@ui/lib/urls'

import {
  Frontmatter,
  getMarkdownBySlug,
  getMarkdownPaths,
} from '@website/utils/markdown'

interface Props {
  frontmatter: Frontmatter
  mdxSource: MDXRemoteSerializeResult
  readingTime: number
}

export async function getStaticPaths() {
  const paths = getMarkdownPaths()

  return {
    paths,
    fallback: 'blocking',
  }
}

export async function getStaticProps({
  params: { slug },
}: {
  params: { slug: string }
}) {
  // Return 404 if the slug doesn't exist
  const markdownData = getMarkdownBySlug(slug)
  if (!markdownData) {
    return {
      notFound: true,
    }
  }

  const { frontmatter, content } = markdownData

  // Calculate reading time
  const wordsPerMinute = 250
  const wordCount = content.split(/\s+/).length
  const readingTime = Math.ceil(wordCount / wordsPerMinute)

  // Format the last_updated_at date
  if (frontmatter.last_updated_at) {
    frontmatter.last_updated_at = format(
      new Date(frontmatter.last_updated_at),
      'MMMM d, yyyy',
    )
  }

  // Replace CTA_LINK_PLACEHOLDER with the cta_uri from the frontmatter
  const contentModified = content.replace(
    'CTA_LINK_PLACEHOLDER',
    frontmatter.cta_uri || '#',
  )

  // Serialize the content to MDX
  const mdxSource = await serialize(contentModified)

  return {
    props: {
      frontmatter,
      mdxSource,
      readingTime,
    },
  }
}

const CustomLink = (props: AnchorHTMLAttributes<HTMLAnchorElement>) => {
  const { href = '', children, ...rest } = props

  if (href.startsWith('/go')) {
    // For /go links, open in a new window
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" {...rest}>
        {children}
      </a>
    )
  } else if (href.startsWith('/')) {
    // For other internal links, use Next.js Link for client-side navigation
    return (
      <Link href={href}>
        <a {...rest}>{children}</a>
      </Link>
    )
  } else {
    // For external links, open in a new window
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" {...rest}>
        {children}
      </a>
    )
  }
}

export function Index({ frontmatter, mdxSource, readingTime }: Props) {
  const title = frontmatter.title_tag || frontmatter.title
  const description = frontmatter.subtitle || ''
  const image = frontmatter.image || ''
  const canonical = canonicalMarketingUrl(
    `/${frontmatter.slug.replace(/^\//, '')}`,
  )

  const getAbsoluteUrl = (url: string) => {
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url
    }
    return canonicalMarketingUrl(url)
  }

  // Generate breadcrumb structured data if tags exist
  const breadcrumbSchema = frontmatter.tags?.length
    ? {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Guides',
            item: canonicalMarketingUrl('/guides'),
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: frontmatter.tags[0],
          },
        ],
      }
    : null

  // Generate article structured data
  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: title,
    description: description,
    image: image ? [getAbsoluteUrl(image)] : undefined,
    datePublished: frontmatter.last_updated_at
      ? new Date(frontmatter.last_updated_at).toISOString()
      : undefined,
    dateModified: frontmatter.last_updated_at
      ? new Date(frontmatter.last_updated_at).toISOString()
      : undefined,
    author: {
      '@type': 'Organization',
      name: 'ATM.com',
      url: canonicalMarketingUrl('/guides'),
    },
    publisher: {
      '@type': 'Organization',
      name: 'ATM.com',
      url: canonicalMarketingUrl(),
    },
  }

  return (
    <>
      <Seo
        title={title}
        description={description}
        canonical={canonical}
        image={image}
      />
      <Head>
        {breadcrumbSchema && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(breadcrumbSchema),
            }}
          />
        )}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(articleSchema),
          }}
        />
      </Head>
      <Container className="pb-8 pt-8 lg:pb-12 lg:pt-12">
        <article className="prose prose-base mx-auto max-w-full">
          <header className="mx-auto mb-6 flex max-w-4xl flex-col items-center justify-center gap-4">
            {frontmatter.tags && (
              <div className="flex w-full flex-row flex-wrap gap-2">
                {frontmatter.tags.map((tag) => (
                  <span
                    key={tag}
                    className="rounded-2xl bg-white px-3 py-1 text-xs text-gray-800"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}
            {!frontmatter.hide_title && frontmatter.title && (
              <h1 className="font-display my-0 w-full text-left text-2xl leading-tight tracking-tight text-[#0a2d4a] sm:text-4xl md:text-5xl lg:text-6xl">
                {frontmatter.title}
              </h1>
            )}
            {!frontmatter.hide_subtitle && frontmatter.subtitle && (
              <h2 className="my-0 w-full text-left text-base font-normal text-[#0a2d4a] sm:text-lg md:text-xl">
                {frontmatter.subtitle}
              </h2>
            )}
            {!frontmatter.hide_author && !frontmatter.hide_date && (
              <div className="mt-4 flex w-full flex-row items-center gap-2">
                <div className="flex w-full flex-col gap-1">
                  {!frontmatter.hide_author && (
                    <div className="text-sm font-bold text-[#0a2d4a]">
                      By{' '}
                      <Link className="font-bold text-[#0a2d4a]" href="/guides">
                        ATM.com
                      </Link>
                    </div>
                  )}
                  {!frontmatter.hide_date && frontmatter.last_updated_at && (
                    <div className="flex flex-row gap-2 text-xs font-bold uppercase tracking-wider text-[#0a2d4a]/70">
                      <span className="date">
                        {frontmatter.last_updated_at}
                      </span>
                      <span>•</span>
                      <span>{String(readingTime)} min read</span>
                    </div>
                  )}
                </div>
                {frontmatter.show_advertiser_disclosure && (
                  <AdvertiserDisclosure className="leading-tight sm:flex-none" />
                )}
              </div>
            )}
            {frontmatter.image && (
              <figure className="my-0">
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src={frontmatter.image}
                  alt={frontmatter.title}
                  className="my-0 w-full rounded-xl"
                />
                {frontmatter.image_caption && (
                  <figcaption className="text-xs text-[#0a2d4a]/70">
                    {frontmatter.image_caption}
                  </figcaption>
                )}
              </figure>
            )}
          </header>
          <div className="prose prose-base prose-headings:font-display prose-headings:mt-8 prose-headings:mb-4 prose-headings:text-[#0a2d4a] prose-p:my-4 prose-p:text-[#0a2d4a] prose-a:text-blueberry-700 prose-a:underline prose-ul:my-4 prose-ul:text-[#0a2d4a] prose-ol:my-4 prose-ol:text-[#0a2d4a] prose-li:text-[#0a2d4a] mx-auto">
            <MDXRemote
              {...mdxSource}
              components={{
                AffiliateDisclosure,
                RecommendedOffer,
                a: CustomLink,
              }}
            />
          </div>
        </article>
      </Container>
    </>
  )
}

Index.getLayout = (page: ReactElement) => (
  <MarketingLayout bg="light-blue">{page}</MarketingLayout>
)

export default Index
